import { useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Icon } from '@/components/ds'
import { toast } from 'sonner'
import { useUser, type WebSession } from '@/contexts/UserContext'
import { ApiError, LOGOUT_MENSAJE_KEY } from '@/lib/api'
import { cleanRut, formatRut, validateRut } from '@/lib/rut'
import { cn } from '@/lib/utils'
import {
  AccesoCuerpo,
  AccesoLayout,
  AccesoPieTarjeta,
  AccesoTitulo,
  AvisoAcceso,
  BotonVerContrasena,
  CampoAcceso,
  claseBotonAcceso,
} from '@/components/auth'

/** Lo que la persona tiene que hacer a continuación, no lo que falló adentro. */
const AYUDA_CREDENCIALES =
  'Revisa el RUT y la contraseña. Si no la recuerdas, usa “¿La olvidaste?”.'

export function Login() {
  const [, navigate] = useLocation()
  const { login, selectEmpresa, sessions } = useUser()
  const [rut, setRut] = useState('')
  const [password, setPassword] = useState('')
  const [verPassword, setVerPassword] = useState(false)
  const [rutTocado, setRutTocado] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // Checkbox "Mantener sesión abierta". Marcado por defecto: es el
  // comportamiento que la app siempre tuvo, y el que espera alguien que entra
  // desde el computador de su oficina.
  const [recordar, setRecordar] = useState(true)
  // Error de credenciales, inline y persistente. El toast se va solo; este se
  // queda hasta que la persona toca un campo.
  const [errorAcceso, setErrorAcceso] = useState<{ titulo: string; detalle?: string } | null>(null)
  // Post-credenciales: el token ya es válido y estamos trayendo empresa y
  // permisos. Es un estado real (dos llamadas al API), no un adorno.
  const [entrando, setEntrando] = useState(false)

  // El 401 revocado deja el motivo en sessionStorage y redirige con
  // `window.location.href` (navegación completa): sonner no sobrevive al
  // desmontaje, así que el toast se muestra acá, ya montada la pantalla de
  // destino. Se consume una sola vez — un reload posterior no debe repetirlo.
  //
  // Este SÍ va por toast y no por `AvisoAcceso`: no es un error del formulario
  // que se corrija tipeando, es un hecho que ocurrió en otro lado.
  useEffect(() => {
    const mensaje = sessionStorage.getItem(LOGOUT_MENSAJE_KEY)
    if (!mensaje) return
    sessionStorage.removeItem(LOGOUT_MENSAJE_KEY)
    toast.error(mensaje, { duration: 8000 })
  }, [])

  // Forzar el formulario de credenciales aunque el navegador ya conozca
  // sesiones (cuando el usuario elige "Ingresar con otra cuenta").
  const [showCredentials, setShowCredentials] = useState(false)

  // El selector se muestra cuando el navegador ya conoce varias empresas: ya sea
  // por un login fresco (N>1) o por una pestaña nueva/recarga que rehidrató la
  // lista desde el almacenamiento. Esto último es lo que permite reanudar en una
  // pestaña nueva sin volver a tipear las credenciales.
  const isSelectingEmpresa = !showCredentials && sessions.length > 1

  const rutValido = validateRut(rut)
  const rutError = rutTocado && rut.length > 0 && !rutValido ? 'Ese RUT no es válido' : ''
  const puedeEnviar = rutValido && password.length > 0

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    // El botón se ve inactivo pero SÍ recibe el click: es acá donde le
    // decimos a la persona qué le falta, en vez de dejarla apretando un botón
    // muerto.
    if (!rutValido) {
      setRutTocado(true)
      setErrorAcceso(
        rut.length === 0
          ? { titulo: 'Falta tu RUT', detalle: 'Es el RUT con que te registraron en tu empresa.' }
          : { titulo: 'Ese RUT no es válido', detalle: 'Revisa los números y el dígito verificador.' }
      )
      return
    }
    if (!password) {
      setErrorAcceso({
        titulo: 'Falta tu contraseña',
        detalle: 'Si no la recuerdas, usa “¿La olvidaste?”.',
      })
      return
    }

    setIsLoading(true)
    setErrorAcceso(null)

    try {
      const { sessions } = await login(rut, password, recordar)

      if (sessions.length === 1) {
        // Auto-selected by UserContext — go straight to dashboard
        setEntrando(true)
        navigate('/')
      } else {
        // Multiple empresas — el selector se deriva del contexto; solo nos
        // aseguramos de salir del modo "ingresar con otra cuenta".
        setShowCredentials(false)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          // 403 = identidad válida pero acceso vedado (empresa bloqueada, etc.).
          // El servidor manda el motivo específico; lo mostramos tal cual y NO
          // le sumamos la ayuda de credenciales: acá el RUT y la clave estaban
          // bien, mandarla a revisarlos sería hacerla dar vueltas.
          setErrorAcceso({ titulo: err.message || 'No tienes acceso al panel web.' })
        } else if (err.status === 422) {
          setErrorAcceso({
            titulo: 'RUT o contraseña incorrectos',
            detalle: AYUDA_CREDENCIALES,
          })
        } else {
          setErrorAcceso({
            titulo: 'No pudimos verificar tu acceso',
            detalle: 'Ocurrió un error inesperado. Intenta nuevamente en unos segundos.',
          })
        }
      } else {
        setErrorAcceso({
          titulo: 'No se pudo conectar con el servidor',
          detalle: 'Revisa tu conexión a internet y vuelve a intentar.',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectEmpresa = async (session: WebSession) => {
    if (!session.empresa) return
    setEntrando(true)
    try {
      await selectEmpresa(session.empresa.id)
      navigate('/')
    } catch {
      setEntrando(false)
      toast.error('No se pudo seleccionar la empresa. Intenta nuevamente.')
    }
  }

  // --- Entrando al panel ---------------------------------------------------
  if (entrando) {
    return (
      <AccesoLayout>
        <div className="flex flex-col items-center gap-3 px-[26px] py-11 text-center">
          <Icon name="progress_activity" size={30} className="text-primary animate-spin" />
          <span className="text-[15px] font-semibold">Entrando al panel…</span>
          <span className="text-muted-foreground text-[12.5px]">
            Cargando la empresa activa y tus permisos.
          </span>
        </div>
      </AccesoLayout>
    )
  }

  // --- Selector de empresa -------------------------------------------------
  if (isSelectingEmpresa) {
    return (
      <AccesoLayout>
        <AccesoCuerpo>
          <AccesoTitulo
            titulo="Elige tu empresa"
            detalle={`Tu RUT tiene acceso a ${sessions.length} empresas. Cada una tiene sus propios documentos y trabajadores.`}
          />

          <div className="flex flex-col gap-2">
            {sessions.map((session) => {
              if (!session.empresa) return null
              const { id, nombre, rut: rutEmpresa } = session.empresa
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelectEmpresa(session)}
                  className="border-input hover:border-primary-border hover:bg-primary-50 flex items-center gap-[11px] rounded-[9px] border px-[13px] py-[11px] text-left transition-colors"
                >
                  <span className="bg-primary-100 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <Icon name="domain" size={19} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-px">
                    <span className="fc-truncate text-[13.5px] font-medium">{nombre}</span>
                    <span className="fc-num text-soft text-[11.5px]">{formatRut(rutEmpresa)}</span>
                  </span>
                  <Icon name="chevron_right" size={18} className="text-soft" />
                </button>
              )
            })}
          </div>
        </AccesoCuerpo>

        <AccesoPieTarjeta className="justify-center gap-[7px]">
          <span className="text-muted-foreground text-[12.5px]">¿No es tu cuenta?</span>
          <button
            type="button"
            onClick={() => setShowCredentials(true)}
            className="text-primary-hover hover:text-primary-strong cursor-pointer text-[12.5px] font-medium"
          >
            Ingresar con otra
          </button>
        </AccesoPieTarjeta>
      </AccesoLayout>
    )
  }

  // --- Credenciales (vista por defecto) ------------------------------------
  return (
    <AccesoLayout>
      <form onSubmit={handleLogin} noValidate>
        <AccesoCuerpo>
          <AccesoTitulo
            titulo="Inicia sesión"
            detalle="Ingresa con el RUT con que te registraron en tu empresa."
          />

          {errorAcceso && <AvisoAcceso titulo={errorAcceso.titulo} detalle={errorAcceso.detalle} />}

          <div className="flex flex-col gap-3.5">
            <CampoAcceso
              id="rut"
              label="RUT"
              icono="badge"
              mono
              placeholder="12.345.678-5"
              autoComplete="username"
              autoCapitalize="characters"
              inputMode="text"
              value={formatRut(rut)}
              onChange={(v) => {
                setRut(cleanRut(v))
                setErrorAcceso(null)
              }}
              onBlur={() => setRutTocado(true)}
              listo={rutValido}
              error={rutError}
              disabled={isLoading}
            />

            <CampoAcceso
              id="password"
              label="Contraseña"
              icono="lock"
              type={verPassword ? 'text' : 'password'}
              placeholder="Tu contraseña"
              autoComplete="current-password"
              value={password}
              onChange={(v) => {
                setPassword(v)
                setErrorAcceso(null)
              }}
              disabled={isLoading}
              accion={
                <Link
                  href="/recuperar-contrasena"
                  className="text-primary-hover hover:text-primary-strong text-[11.5px]"
                >
                  ¿La olvidaste?
                </Link>
              }
              sufijo={
                <BotonVerContrasena
                  visible={verPassword}
                  onToggle={() => setVerPassword((v) => !v)}
                />
              }
            />

            <div className="flex items-center gap-[9px] self-start">
              <Checkbox
                id="recordar"
                checked={recordar}
                onCheckedChange={(v) => setRecordar(v === true)}
                disabled={isLoading}
              />
              <Label htmlFor="recordar" className="text-body cursor-pointer font-normal">
                Mantener sesión abierta en este equipo
              </Label>
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            className={cn('w-full', claseBotonAcceso(puedeEnviar))}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Icon name="progress_activity" size={18} className="animate-spin" />
                Verificando…
              </>
            ) : (
              <>
                Iniciar sesión
                <Icon name="arrow_forward" size={18} />
              </>
            )}
          </Button>
        </AccesoCuerpo>
      </form>

      <AccesoPieTarjeta className="justify-center gap-[7px]">
        <span className="text-muted-foreground text-[12.5px]">
          ¿Tu empresa todavía no tiene cuenta?
        </span>
        <Link
          href="/registro"
          className="text-primary-hover hover:text-primary-strong text-[12.5px] font-medium"
        >
          Crear cuenta
        </Link>
      </AccesoPieTarjeta>
    </AccesoLayout>
  )
}
